import { BrokerId } from '@demeter/lib/models/broker';
import { SignalObservation, SignalOverride } from '@demeter/lib/models/signal';

export type TraderRunKind = 'ORDERS' | 'PLANS' | 'DIRECTIVES';

export interface TraderRunSignal {
	signalId: string;
	correlationId: string;
	data: string;
}

export interface TraderRunTarget {
	symbol: string;
	weight: number;
	price: number;
	shareQty: number;
	// C# source: double? HeldQty (Common's TraderRunDTO.cs) — the share count held when the run
	// started. Null/absent means the run recorded no starting portfolio (Oasis/Notification runs,
	// and every run written before this field shipped); 0 means confirmed flat. Never coerce to 0
	// — "position already equals target" and "nothing was placed" are told apart on exactly this
	// difference.
	//
	// OPTIONAL, not `number | null`, for two reasons. (1) Older runs omit the key entirely, and
	// Core.Trader's GET /runs path serializes through WorkerOptions.Serializer — unassigned, so
	// it falls back to JsonSerializerDefaults.Web with NO ignore condition, which writes nulls
	// today. If that is ever "cleaned up" to use the configured options
	// (Program.cs:44-48 sets WhenWritingDefault), a null heldQty would start dropping and a
	// required type would be lying. (2) Three existing spec fixtures build TraderRunTarget
	// literals without it; a required property reds the build across a task boundary.
	//
	// Read it as `t.heldQty == null` — LOOSE equality, so it catches undefined too.
	heldQty?: number | null;
}

export interface TraderRunOrder {
	orderId: string;
	symbol: string;
	orderSide: string;
	orderType: string;
	qty: number;
	filledQty: number;
	averageFillPrice: number;
	orderStatus: string;
	sliceIds: string[];
	// C# source: TraderRunOptionLadder? Ladder (Common's TraderRunDTO.cs), Core.Trader#273. Null on
	// every order that is not an option buy past the limit cutoff; set by getRuns'
	// normalizeRunOrderLadder. Optional so the spec fixtures that build orders need not carry it.
	ladder?: TraderRunOptionLadder | null;
}

// C# source: TraderRunOptionLadderStep (Common's TraderRunDTO.cs), Core.Trader#273. One broker order
// a laddered buy was worked as, in placement order: `step` 1–4, null for the MARKET order; `qty` the
// remainder it was placed for.
export interface TraderRunOptionLadderStep {
	step: number | null;
	orderType: string;
	orderId: string;
	price: number | null;
	placedAtUtc: string;
	qty: number;
	filledQty: number;
	averageFillPrice: number | null;
	orderStatus: string;
}

// C# source: TraderRunOptionLadder. `skipReason` (an OptionLadderSkipReasons string) set means the buy
// went MARKET from the start; null means it laddered and `ending` (an OptionLadderEndings string) says
// how it ended. Strings, never enums: an unknown value renders neutrally.
export interface TraderRunOptionLadder {
	startAsk: number | null;
	startFairValue: number | null;
	skipReason: string | null;
	ending: string | null;
	endingStep: number | null;
	steps: TraderRunOptionLadderStep[];
}

// GET /runs writes with WhenWritingDefault, so a ladder arrives without its nulls and zeros.
export function normalizeRunOrderLadder(order: TraderRunOrder): TraderRunOrder {
	const raw = order.ladder;
	if (raw == null) {
		return { ...order, ladder: null };
	}
	return {
		...order,
		ladder: {
			startAsk: raw.startAsk ?? null,
			startFairValue: raw.startFairValue ?? null,
			skipReason: raw.skipReason ?? null,
			ending: raw.ending ?? null,
			endingStep: raw.endingStep ?? null,
			steps: (raw.steps ?? []).map(s => ({
				...s,
				step: s.step ?? null,
				price: s.price ?? null,
				qty: s.qty ?? 0,
				filledQty: s.filledQty ?? 0,
				averageFillPrice: s.averageFillPrice ?? null
			}))
		}
	};
}

export interface TraderRunDirective {
	kind: string;
	planKey: string;
	stepIndex: number;
	// C# source: DateTime? NotBefore (Common's TraderRunDTO.cs) — null means "unscheduled",
	// which is the NORMAL case for every existing producer today, not an edge case. A caller
	// that formats this unconditionally (e.g. new Date(directive.notBefore)) must handle null.
	notBefore: string | null;
	reason: string;
	outcome: string;
}

// C# source: TraderRunEquityAdjustment (Common's TraderRunDTO.cs), Core.Trader#185.
export interface TraderRunEquityAdjustment {
	adjustmentId: string;
	sourceFundId: string;
	// Core.Trader#193: the row's account; "" on runs recorded before the field.
	brokerId: string;
	brokerAccountId: string;
	rowAmount: number;
	// null = the run fund's own row; otherwise the 0–1 stake the source fund's row reached it at.
	stake: number | null;
	appliedAmount: number;
	rangeStart: string;
	rangeEnd: string;
	note: string | null;
}

export interface TraderRun {
	runKey: string;
	sessionDate: string;
	fundId: string;
	// brokerLabel(id: BrokerId) is enum-typed — narrow at the call site.
	brokerId: BrokerId | string;
	brokerAccountId: string;
	strategyId: string;
	kind: TraderRunKind | string;
	completedAtUtc: string;
	submittedBy: 'signal' | 'manual' | string;
	correlationIds: string[];
	signals: TraderRunSignal[];
	overrides: SignalOverride[];
	target: TraderRunTarget[];
	// C# source: double? SizingEquity / MinTradeThreshold (Common's TraderRunDTO.cs). Per-RUN
	// scalars (not per-target-row) recording the equity base and threshold BuildOrders actually
	// used, so below-threshold skips can be told apart from unexplained ones. Optional/nullable
	// for the same reason as heldQty: older runs predate the field.
	sizingEquity?: number | null;
	minTradeThreshold?: number | null;
	// C# source: double? PreAdjustmentEquity (Common's TraderRunDTO.cs), Core.Trader#185. The sizing
	// base before equity adjustments; null when none applied and on runs recorded before the field.
	preAdjustmentEquity?: number | null;
	// Normalised to [] by CoreTraderApiService.getRuns, so non-optional here.
	equityAdjustments: TraderRunEquityAdjustment[];
	orders: TraderRunOrder[];
	// Plan KEYS (C# TraderRunDTO.PlanIds), not planId GUIDs — join on PlanSummary.planKey, exactly.
	planIds: string[];
	directives: TraderRunDirective[];
}

export interface TraderIncident {
	incidentId: string;
	fundId: string;
	brokerId: string;
	brokerAccountId: string;
	strategyId: string;
	signalId: string | null;
	correlationId: string;
	kind: string;
	exceptionMessage: string;
	failedAtUtc: string;
	sessionDate: string;
	deliveryCount: number;
}

// lib/models/signal.ts's SignalObservation already mirrors signalId/observedAt/sessionDate/
// runKind: RunKind/isPublished/signalData.
export interface SessionObservation extends SignalObservation {
	scheduleToken: string | null;
	funcId: string;
	correlationId: string | null;
	createdAt: string;
	isDecline: boolean;
}

export type FeedEventKind = 'signal-observed' | 'run-completed' | 'incident';

export interface FeedEvent {
	kind: FeedEventKind | string;
	sessionDate: string;
	signalId: string | null;
	runKey: string | null;
	incidentId: string | null;
	at: string;
}

export interface NegotiateResponse {
	url: string;
	accessToken: string;
}
